"use strict";
/**
 * Crypto provides KILT with the utility types and methods useful for cryptographic operations, such as signing/verifying, encrypting/decrypting and hashing.
 *
 * The utility types and methods are wrappers for existing Polkadot functions and imported throughout KILT's protocol for various cryptographic needs.
 *
 * @packageDocumentation
 * @module Crypto
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.hashStatements = exports.saltedBlake2b256 = exports.decryptAsymmetricAsStr = exports.decryptAsymmetric = exports.encryptAsymmetricAsStr = exports.encryptAsymmetric = exports.hashObjectAsStr = exports.hashStr = exports.hash = exports.decryptSymmetricStr = exports.decryptSymmetric = exports.encryptSymmetricAsStr = exports.encryptSymmetric = exports.verify = exports.signStr = exports.sign = exports.coToUInt8 = exports.u8aConcat = exports.u8aToHex = exports.decodeAddress = exports.encodeAddress = void 0;
const tslib_1 = require("tslib");
const keyring_1 = require("@polkadot/keyring");
Object.defineProperty(exports, "decodeAddress", { enumerable: true, get: function () { return keyring_1.decodeAddress; } });
Object.defineProperty(exports, "encodeAddress", { enumerable: true, get: function () { return keyring_1.encodeAddress; } });
const util_1 = require("@polkadot/util");
Object.defineProperty(exports, "u8aConcat", { enumerable: true, get: function () { return util_1.u8aConcat; } });
Object.defineProperty(exports, "u8aToHex", { enumerable: true, get: function () { return util_1.u8aToHex; } });
const util_crypto_1 = require("@polkadot/util-crypto");
const asU8a_1 = require("@polkadot/util-crypto/blake2/asU8a");
const decrypt_1 = require("@polkadot/util-crypto/nacl/decrypt");
const encrypt_1 = require("@polkadot/util-crypto/nacl/encrypt");
const tweetnacl_1 = tslib_1.__importDefault(require("tweetnacl"));
const uuid_1 = require("uuid");
const jsonabc_1 = tslib_1.__importDefault(require("./jsonabc"));
/**
 * Creates a Uint8Array value from a Uint8Array, Buffer, string or hex input.
 *
 * @param input Input array or string. Null or undefined result in an empty array.
 * @param hexAsString Whether or not a hex string is encoded as a string instead of a number.
 * @returns A (possibly empty) Uint8Array.
 */
function coToUInt8(input, hexAsString = false) {
    if (hexAsString && util_1.isString(input)) {
        return util_1.stringToU8a(input);
    }
    return util_1.u8aToU8a(input);
}
exports.coToUInt8 = coToUInt8;
/**
 * Signs a message.
 *
 * @param message String or byte array to be signed.
 * @param signKeyPair KeyringPair used for signing.
 * @returns Signature over message as byte array.
 */
function sign(message, signKeyPair) {
    return signKeyPair.sign(coToUInt8(message), { withType: true });
}
exports.sign = sign;
/**
 * Signs a message. Returns signature string.
 *
 * @param message String or byte array to be signed.
 * @param signKeyPair KeyringPair used for signing.
 * @returns Signature over message as hex string.
 */
function signStr(message, signKeyPair) {
    return util_1.u8aToHex(sign(message, signKeyPair));
}
exports.signStr = signStr;
/**
 * Verifies a signature over a message.
 *
 * @param message Original signed message to be verified.
 * @param signature Signature as hex string or byte array.
 * @param address Substrate address or public key of the signer.
 * @returns Whether the signature could be verified.
 */
function verify(message, signature, address) {
    return util_crypto_1.signatureVerify(message, signature, address).isValid === true;
}
exports.verify = verify;
/**
 * Encrypts a with a secret/key, which can be decrypted using the same key.
 *
 * @param message Message to be encrypted.
 * @param secret Secret key for encryption & decryption.
 * @param nonce Random nonce to obscure message contents when encrypted. Will be auto-generated if not supplied.
 * @returns Encrypted message & nonce used for encryption. Both are needed for decryption.
 */
function encryptSymmetric(message, secret, nonce) {
    return encrypt_1.naclEncrypt(coToUInt8(message, true), coToUInt8(secret), nonce ? coToUInt8(nonce) : undefined);
}
exports.encryptSymmetric = encryptSymmetric;
/**
 * Encrypts a with a secret/key, which can be decrypted using the same key.
 *
 * @param message Message to be encrypted.
 * @param secret Secret key for encryption & decryption.
 * @param inputNonce Random nonce to obscure message contents that could otherwise be guessed or inferred. Will be auto-generated if not supplied.
 * @returns Encrypted message as string & nonce used for encryption as hex strings. Both are needed for decryption.
 */
function encryptSymmetricAsStr(message, secret, inputNonce) {
    const result = encrypt_1.naclEncrypt(coToUInt8(message, true), coToUInt8(secret), inputNonce ? coToUInt8(inputNonce) : undefined);
    const nonce = util_1.u8aToHex(result.nonce);
    const encrypted = util_1.u8aToHex(result.encrypted);
    return { encrypted, nonce };
}
exports.encryptSymmetricAsStr = encryptSymmetricAsStr;
/**
 * Takes a nonce & secret to decrypt a message.
 *
 * @param data Object containing encrypted message and the nonce used for encryption.
 * @param secret Secret key for encryption & decryption.
 * @returns Decrypted message as byte array.
 */
function decryptSymmetric(data, secret) {
    return decrypt_1.naclDecrypt(coToUInt8(data.encrypted), coToUInt8(data.nonce), coToUInt8(secret));
}
exports.decryptSymmetric = decryptSymmetric;
/**
 * Takes a nonce & secret to decrypt a message.
 *
 * @param data Object containing encrypted message and the nonce used for encryption.
 * @param secret Secret key for encryption & decryption.
 * @returns Decrypted message as string.
 */
function decryptSymmetricStr(data, secret) {
    const result = decrypt_1.naclDecrypt(coToUInt8(data.encrypted), coToUInt8(data.nonce), coToUInt8(secret));
    return result ? util_1.u8aToString(result) : null;
}
exports.decryptSymmetricStr = decryptSymmetricStr;
/**
 * Create the blake2b and return the result as a u8a with the specified `bitLength`.
 *
 * @param value Value to be hashed.
 * @param bitLength Bit length of hash.
 * @returns Blake2b hash byte array.
 */
function hash(value, bitLength) {
    return asU8a_1.blake2AsU8a(value, bitLength);
}
exports.hash = hash;
/**
 * Create the blake2b and return the result as a hex string.
 *
 * @param value Value to be hashed.
 * @returns Blake2b hash as hex string.
 */
function hashStr(value) {
    return util_1.u8aToHex(hash(value));
}
exports.hashStr = hashStr;
/**
 * Hashes numbers, booleans, and objects by stringifying them. Object keys are sorted to yield consistent hashing.
 *
 * @param value Object or value to be hashed.
 * @param nonce Optional nonce to obscure hashed values that could be guessed.
 * @returns Blake2b hash as hex string.
 */
function hashObjectAsStr(value, nonce) {
    let input = 
    // eslint-disable-next-line no-nested-ternary
    typeof value === 'object' && value !== null
        ? JSON.stringify(jsonabc_1.default.sortObj(value))
        : // eslint-disable-next-line no-nested-ternary
            typeof value === 'number' && value !== null
                ? value.toString()
                : typeof value === 'boolean' && value !== null
                    ? JSON.stringify(value)
                    : value;
    if (nonce) {
        input = nonce + input;
    }
    return hashStr(input);
}
exports.hashObjectAsStr = hashObjectAsStr;
/**
 * Wrapper around nacl.box. Authenticated encryption of a message for a recipient's public key.
 *
 * @param message String or byte array to be enrypted.
 * @param publicKeyA Public key of the recipient. The owner will be able to decrypt the message.
 * @param secretKeyB Private key of the sender. Necessary to authenticate the message during decryption.
 * @returns Encrypted message and nonce used for encryption.
 */
function encryptAsymmetric(message, publicKeyA, secretKeyB) {
    const nonce = tweetnacl_1.default.randomBytes(24);
    const box = tweetnacl_1.default.box(coToUInt8(message, true), nonce, coToUInt8(publicKeyA), coToUInt8(secretKeyB));
    return { box, nonce };
}
exports.encryptAsymmetric = encryptAsymmetric;
/**
 * Wrapper around nacl.box. Authenticated encryption of a message for a recipient's public key.
 *
 * @param message String or byte array to be enrypted.
 * @param publicKeyA Public key of the recipient. The owner will be able to decrypt the message.
 * @param secretKeyB Private key of the sender. Necessary to authenticate the message during decryption.
 * @returns Encrypted message and nonce used for encryption as hex strings.
 */
function encryptAsymmetricAsStr(message, publicKeyA, secretKeyB) {
    const encrypted = encryptAsymmetric(message, publicKeyA, secretKeyB);
    const box = util_1.u8aToHex(encrypted.box);
    const nonce = util_1.u8aToHex(encrypted.nonce);
    return { box, nonce };
}
exports.encryptAsymmetricAsStr = encryptAsymmetricAsStr;
/**
 * Wrapper around nacl.box.open. Authenticated decryption of an encrypted message.
 *
 * @param data Object containing encrypted message and nonce used for encryption.
 * @param publicKeyB Public key of the sender. Necessary to authenticate the message during decryption.
 * @param secretKeyA Private key of the recipient. Required for decryption.
 * @returns Decrypted message or false if decryption is unsuccessful.
 */
function decryptAsymmetric(data, publicKeyB, secretKeyA) {
    const decrypted = tweetnacl_1.default.box.open(coToUInt8(data.box), coToUInt8(data.nonce), coToUInt8(publicKeyB), coToUInt8(secretKeyA));
    return decrypted || false;
}
exports.decryptAsymmetric = decryptAsymmetric;
/**
 * Wrapper around nacl.box.open. Authenticated decryption of an encrypted message.
 *
 * @param data Object containing encrypted message and nonce used for encryption.
 * @param publicKeyB Public key of the sender. Necessary to authenticate the message during decryption.
 * @param secretKeyA Private key of the recipient. Required for decryption.
 * @returns Decrypted message as string or false if decryption is unsuccessful.
 */
function decryptAsymmetricAsStr(data, publicKeyB, secretKeyA) {
    const result = decryptAsymmetric(data, coToUInt8(publicKeyB), coToUInt8(secretKeyA));
    return result ? util_1.u8aToString(result) : false;
}
exports.decryptAsymmetricAsStr = decryptAsymmetricAsStr;
/**
 * Default hasher for [[hashStatements]].
 *
 * @param value String to be hashed.
 * @param nonce Optional nonce (as string) used to obscure hashed contents.
 * @returns 256 bit blake2 hash as hex string.
 */
exports.saltedBlake2b256 = (value, nonce) => util_crypto_1.blake2AsHex((nonce || '') + value, 256);
/**
 * Configurable computation of salted over an array of statements. Can be used to validate/reproduce salted hashes
 * by means of an optional nonce map.
 *
 * @param statements An array of statement strings to be hashed.
 * @param options Optional hasher arguments.
 * @param options.nonces An optional map or array of nonces. If present, it should comprise all keys of `statements`, as those will be used map nonces to statements.
 * @param options.nonceGenerator An optional nonce generator. Will be used if `options.nonces` is not defined to generate a (new) nonce for each statement. The statement key is passed as its first argument. If no `nonces` or `nonceGenerator` are given this function returns unsalted hashes.
 * @param options.hasher The hasher to be used. Computes a hash from a statement and an optional nonce. Required but defaults to 256 bit blake2 over `${nonce}${statement}`.
 * @returns An array of objects for each statement which contain a statement, its digest, salted hash and nonce.
 */
function hashStatements(statements, options = {}) {
    // apply defaults
    const defaults = {
        hasher: exports.saltedBlake2b256,
        nonceGenerator: () => uuid_1.v4(),
    };
    const hasher = options.hasher || defaults.hasher;
    const nonceGenerator = options.nonceGenerator || defaults.nonceGenerator;
    // set source for nonces
    const { nonces } = options;
    const getNonce = typeof nonces === 'object' ? (key) => nonces[key] : nonceGenerator;
    // iterate over statements to produce salted hashes
    return statements.map((statement) => {
        // generate unsalted digests from statements as a first step
        const digest = hasher(statement);
        // if nonces were passed, they would be mapped to the statement via its digest
        const nonce = getNonce(digest);
        // to simplify validation, the salted hash is computed over unsalted hash (nonce key) & nonce
        const saltedHash = hasher(digest, nonce);
        return { digest, saltedHash, nonce, statement };
    });
}
exports.hashStatements = hashStatements;
